"use client";

import { AlertCircleIcon } from "lucide-react";
import { useState } from "react";

import type { ErrorInfo } from "../../types";
import { cn } from "../../utils";
import { CollapsibleSection } from "../CollapsibleSection";

// ---------------------------------------------------------------------------
// ErrorBlock — shared error display component
//
// Supports two modes:
// 1. Simple inline error (no verbose details) — renders a styled error badge
// 2. Collapsible error (has verbose details) — renders a CollapsibleSection
//    with the short message as the label and verbose content in the body
//
// Used by both Vibes and promptlayer_app/frontend to render error states
// in chat messages.
// ---------------------------------------------------------------------------

interface ErrorBlockProps {
  /** The error to display. Can be an ErrorInfo object or a plain string. */
  error: ErrorInfo | string | undefined | null;
  /** Extra className on the outer wrapper. */
  className?: string;
}

export const ErrorBlock = ({ error, className }: ErrorBlockProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!error) {return null;}

  // Normalize string errors into ErrorInfo shape
  const normalized: ErrorInfo =
    typeof error === "string" ? { message: error } : error;

  const hasVerbose = Boolean(normalized.verbose);

  // Simple inline error — no collapsible needed
  if (!hasVerbose) {
    return (
      <div
        className={cn(
          "flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5",
          className,
        )}
      >
        <AlertCircleIcon className="h-3.5 w-3.5 flex-shrink-0 text-red-500" />
        <p className="whitespace-pre-wrap text-xs text-red-700">
          {normalized.message}
        </p>
      </div>
    );
  }

  // Collapsible error with verbose details
  return (
    <CollapsibleSection
      icon={
        <AlertCircleIcon className="h-3.5 w-3.5 min-w-3.5 flex-shrink-0 text-red-500" />
      }
      label={normalized.message ?? "Error"}
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded((prev) => !prev)}
      variant="error"
      copyText={normalized.verbose ?? ""}
      className={className}
    >
      <p className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-red-700">
        {normalized.verbose}
      </p>
    </CollapsibleSection>
  );
};
